import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import DroneFormFields from 'components/forms/DroneFormFields';

import useDrone from 'hooks/useDrone';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { CreateDroneFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateDroneForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    onCreated,
    requestingClose,
    testId = TEST_ID
}) => {
    const { createDrone, isLoading } = useDrone();
    const { CREATE } = useTranslation();
    const navigate = useNavigate();
    const { droneDetailsPage } = usePageRoutes();

    const form = useForm<CreateDroneFormSchema>({
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = async (data: CreateDroneFormSchema) => {
        const { faaRegistrationNumber, name } = data;

        const drone = await createDrone({ drone: { faaRegistrationNumber, name } });

        if (drone) {
            onCreated?.();
            navigate(droneDetailsPage(drone.id));
        }
    };

    return (
        <VerticalContainer className={`${styles.createDroneForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <DroneFormFields />

                    <FormSubmitPanel
                        buttonText={CREATE}
                        disabled={isLoading}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateDroneForm;
